/* eslint-disable jira/import-whitelist */
import { loadQuery, type PreloadedQuery } from 'react-relay';
import { createAri } from '@atlassian/jira-platform-ari/src/index.tsx';
import getRelayEnvironment from '@atlassian/jira-relay-environment/src/index.tsx';
import { QueryPromisesMap } from '@atlassian/relay-query-promises';
import { RELAY_RESOURCE_TYPE } from '@atlassian/jira-relay-utils/src/services/resources/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import uiVersionDetailsQuery from '@atlassian/jira-relay/src/__generated__/uiVersionDetailsQuery$parameters';
import type {
	uiVersionDetailsQuery$variables,
	uiVersionDetailsQuery as uiVersionDetailsQueryType,
} from '@atlassian/jira-relay/src/__generated__/uiVersionDetailsQuery.graphql';
import { isDevopsFeatureDisabledInFedRamp } from '@atlassian/jira-software-devops-fedramp-utils/src/index.tsx';
import { toVersionIssuesGraphQLVariables } from '@atlassian/jira-software-releases-version-detail-issue-list-query-param-controller/src/controllers/graphql-variables/index.tsx';
import { checkAuthentication } from '@atlassian/jira-spa-apps-common/src/interceptors/authentication/index.tsx';
import { createResource, type RouteResource } from '@atlassian/react-resource-router';
import { RESOURCE_TYPE_PROJECTS_VERSION_DETAILS } from '../constants.tsx';

// Only used when isVersionDetailsCustomRouteEnabled on
export const versionDetailsRelayResourceCustom: RouteResource<
	PreloadedQuery<uiVersionDetailsQueryType, Record<string, unknown>>
> = createResource<PreloadedQuery<uiVersionDetailsQueryType>>({
	type: `${RELAY_RESOURCE_TYPE}_${RESOURCE_TYPE_PROJECTS_VERSION_DETAILS}`,
	// Deliberately returning the static key, not to rerun query by resource, as different key returns null queryReference shortly while the query is running, when filter query parameter is changed,
	// which is believed to be a bug in react-resource-router part, but we don't know the reason yet.
	// Context: https://atlassian.slack.com/archives/CFLN3FEF8/p1680495188086669
	// Due to the same bug, issue navigator team is doing the similar technique: src/packages/routes/src/ui/spa/issue-navigator/resources/index.tsx
	getKey: ({ match }) => {
		const { versionId } = match.params;
		const parsedVersionId = String(versionId ?? '').replace(/\D/g, '');

		return `jsw-releases-version-details-main-query-relay-${parsedVersionId}`;
	},
	getData: async ({ match, query }, { tenantContext }) => {
		const { versionId, projectKey } = match.params;
		const { filters, sortBy } = toVersionIssuesGraphQLVariables(query);
		const parsedVersionId = String(versionId ?? '').replace(/\D/g, '');

		// eslint-disable-next-line jira/ff/inline-usage
		const variables: uiVersionDetailsQuery$variables = {
			cloudAri: createAri({
				resourceOwner: 'jira',
				resourceType: 'site',
				resourceId: tenantContext.cloudId,
			}),
			versionAri: createAri({
				resourceOwner: 'jira',
				cloudId: tenantContext.cloudId,
				resourceType: 'version/activation',
				resourceId: String(`${tenantContext.activationId}/${parsedVersionId}`),
			}),
			sortBy,
			filters,
			skip: !checkAuthentication(tenantContext) || parsedVersionId.length === 0,
			isDevopsFeatureDisabledInFedRamp: isDevopsFeatureDisabledInFedRamp(),
			shouldIncludeDevOpsFieldsViaAgs: !isDevopsFeatureDisabledInFedRamp(),
			cloudId: tenantContext.cloudId,
			projectKey: projectKey || '',
			isReleasesUpdateStatusPermissionFixEnabled: fg('jira-releases-update-status-permission-fix'),
		};

		const queryReference = loadQuery<uiVersionDetailsQueryType>(
			getRelayEnvironment(),
			uiVersionDetailsQuery,
			variables,
			{
				fetchPolicy: 'store-or-network',
			},
		);

		if (__SERVER__ && queryReference.id != null) {
			await QueryPromisesMap.get(queryReference.id);
		}

		return queryReference;
	},
	maxAge: 0,
});
